import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { listPublicPosts } from '../api/client';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../contexts/LanguageContext';

const PLACEHOLDER_IMG =
	'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=450&fit=crop&crop=entropy&auto=format';

const CATEGORIES = ['technology', 'cloud', 'consulting', 'development', 'security', 'automation'];
const LANGS = ['pt', 'en', 'es'];
const PAGE_SIZE = 9;

export default function BlogListing() {
	const { t, language: langCtx } = useLanguage();
	const [searchParams, setSearchParams] = useSearchParams();

	// estado/params
	const [language, setLanguage] = useState(searchParams.get('language') || langCtx || 'pt');
	const [category, setCategory] = useState(searchParams.get('category') || '');
	const [featured, setFeatured] = useState(searchParams.get('featured') || '');
	const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

	// dados
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [err, setErr] = useState('');
	const [hasNext, setHasNext] = useState(false);

	// abort controller para cancelar requisição ao trocar filtros
	const abortRef = useRef(null);

	// sincroniza URL <-> estado
	useEffect(() => {
		const params = {};
		if (language) params.language = language;
		if (category) params.category = category;
		if (featured) params.featured = featured;
		if (page > 1) params.page = String(page);
		setSearchParams(params, { replace: true });
	}, [language, category, featured, page]);

	const filters = useMemo(() => {
		const f = { page, size: PAGE_SIZE, language };
		if (category) f.category = category;
		if (featured === 'true') f.featured = true;
		if (featured === 'false') f.featured = false;
		return f;
	}, [page, language, category, featured]);

	useEffect(() => {
		fetchPosts();
		return () => {
			if (abortRef.current) abortRef.current.abort();
		};
	}, [filters]);

	async function fetchPosts() {
		try {
			setLoading(true);
			setErr('');
			const ctrl = new AbortController();
			abortRef.current = ctrl;

			const data = await listPublicPosts(filters);
			setPosts(Array.isArray(data) ? data : []);

			// como a API pública não retorna total, inferimos se há próxima página
			setHasNext(Array.isArray(data) && data.length === PAGE_SIZE);
		} catch (e) {
			if (e.name === 'CanceledError' || e.name === 'AbortError') return;
			console.error(e);
			setErr(t('blog.errors.loadFailed', 'Erro ao carregar posts.'));
			setPosts([]);
			setHasNext(false);
		} finally {
			setLoading(false);
		}
	}

	const formatDate = (iso) => {
		const dt = new Date(iso);
		if (Number.isNaN(dt.getTime())) return '';
		return dt.toLocaleDateString(t('locale'), { year: 'numeric', month: 'long', day: 'numeric' });
	};

	const translateCategory = (category) => {
		const map = {
			technology: t('blog.categories.technology'),
			cloud: t('blog.categories.cloud'),
			consulting: t('blog.categories.consulting'),
			development: t('blog.categories.development'),
			security: t('blog.categories.security'),
			automation: t('blog.categories.automation'),
		};
		return map[category] || category;
	};

	const getCategoryColor = (category) => {
		const map = {
			[t('blog.categories.technology')]: 'bg-primary/10 text-primary',
			[t('blog.categories.cloud')]: 'bg-blue-100 text-blue-700',
			[t('blog.categories.consulting')]: 'bg-secondary/10 text-secondary',
			[t('blog.categories.development')]: 'bg-green-100 text-green-700',
			[t('blog.categories.security')]: 'bg-red-100 text-red-700',
			[t('blog.categories.automation')]: 'bg-purple-100 text-purple-700',
			technology: 'bg-primary/10 text-primary',
			cloud: 'bg-blue-100 text-blue-700',
			consulting: 'bg-secondary/10 text-secondary',
			development: 'bg-green-100 text-green-700',
			security: 'bg-red-100 text-red-700',
			automation: 'bg-purple-100 text-purple-700',
		};
		return map[category] || 'bg-gray-100 text-gray-700';
	};

	return (
		<section className="py-24 bg-white">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

				{/* Header */}
				<div className="text-center mb-10">
					<h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
						{t('blog.listing.title', 'Blog')}
					</h1>
					<p className="text-lg text-gray-600">
						{t('blog.listing.subtitle', 'Artigos e novidades da Hack a Tech')}
					</p>
					{err && (
						<div className="mt-4 flex items-center justify-center text-amber-600">
							<AlertCircle className="w-5 h-5 mr-2" />
							<span className="text-sm">{err}</span>
						</div>
					)}
				</div>

				{/* Filters */}
				<div className="bg-gray-50 border rounded-xl p-4 mb-8 flex flex-wrap items-end gap-3">
					<div>
						<label className="block text-sm text-gray-600 mb-1">
							{t('blog.filters.language', 'Idioma')}
						</label>
						<select
							value={language}
							onChange={(e) => { setPage(1); setLanguage(e.target.value); }}
							className="border rounded-lg px-3 py-2"
						>
							{LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
						</select>
					</div>

					<div>
						<label className="block text-sm text-gray-600 mb-1">
							{t('blog.filters.category', 'Categoria')}
						</label>
						<select
							value={category}
							onChange={(e) => { setPage(1); setCategory(e.target.value); }}
							className="border rounded-lg px-3 py-2"
						>
							<option value="">{t('blog.filters.all', '(todas)')}</option>
							{CATEGORIES.map(c => <option key={c} value={c}>{translateCategory(c)}</option>)}
						</select>
					</div>

					<div>
						<label className="block text-sm text-gray-600 mb-1">
							{t('blog.filters.featured', 'Destaque')}
						</label>
						<select
							value={featured}
							onChange={(e) => { setPage(1); setFeatured(e.target.value); }}
							className="border rounded-lg px-3 py-2"
						>
							<option value="">{t('blog.filters.any', '(qualquer)')}</option>
							<option value="true">{t('blog.filters.yes', 'Sim')}</option>
							<option value="false">{t('blog.filters.no', 'Não')}</option>
						</select>
					</div>
				</div>

				{/* Grid */}
				{loading ? (
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
						{Array.from({ length: PAGE_SIZE }).map((_, i) => (
							<Card key={i} className="animate-pulse">
								<div className="w-full h-48 bg-gray-200 rounded-t-lg" />
								<CardContent className="p-6 space-y-3">
									<div className="h-4 bg-gray-200 rounded w-1/3" />
									<div className="h-6 bg-gray-200 rounded w-3/4" />
									<div className="h-20 bg-gray-200 rounded" />
								</CardContent>
							</Card>
						))}
					</div>
				) : posts.length === 0 ? (
					<div className="text-center text-gray-500 py-16">
						{t('blog.listing.noResults', 'Nenhum post encontrado com os filtros selecionados.')}
					</div>
				) : (
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
						{posts.map((post) => {
							const dateISO = post.published_at || post.created_at;
							const img = post.image_url || PLACEHOLDER_IMG;

							return (
								<Card key={post.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 overflow-hidden">
									<div className="relative overflow-hidden">
										<img
											src={img}
											alt={post.title}
											className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
											onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
										/>
										<div className="absolute top-4 left-4">
											<span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
												{translateCategory(post.category)}
											</span>
										</div>
										{post.featured && (
											<div className="absolute top-4 right-4">
												<span className="bg-secondary text-white px-2 py-1 rounded-full text-xs font-medium">
													⭐ Featured
												</span>
											</div>
										)}
									</div>

									<CardContent className="p-6">
										<div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
											<div className="flex items-center">
												<Calendar className="w-4 h-4 mr-1" />
												{formatDate(dateISO)}
											</div>
											<div className="flex items-center">
												<Clock className="w-4 h-4 mr-1" />
												{post.read_time_minutes} min
											</div>
										</div>

										<h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300 line-clamp-2">
											{post.title}
										</h3>

										<p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
											{post.excerpt}
										</p>

										<div className="flex items-center justify-between">
											<div className="text-sm text-gray-500">
												{post.author}
											</div>

											<Link
												to={`/blog/${post.slug}`}
												className="flex items-center text-primary hover:text-primary/80 font-medium text-sm group-hover:translate-x-1 transition-transform duration-300"
											>
												{t('blog.readMore')}
												<ArrowRight className="w-4 h-4 ml-1" />
											</Link>
										</div>
									</CardContent>
								</Card>
							);
						})}
					</div>
				)}

				{/* Pagination */}
				{!loading && (
					<div className="flex items-center justify-between mt-10">
						<button
							className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={page <= 1}
						>
							<ArrowLeft className="w-4 h-4" /> {t('blog.pagination.prev', 'Anterior')}
						</button>

						<span className="text-sm text-gray-500">
							{t('blog.pagination.page', 'Página')} {page}
						</span>

						<button
							className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
							onClick={() => setPage((p) => p + 1)}
							disabled={!hasNext}
						>
							{t('blog.pagination.next', 'Próxima')} <ArrowRight className="w-4 h-4" />
						</button>
					</div>
				)}
			</div>
		</section>
	);
}
